import { useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import PresentationModal from '../../organisms/PresentationModal';
import { getGuarantees, updateGuarantee } from './collateralStore';
import {
    formatCurrency,
    getGuaranteeStatusColor,
    getIntensityColor,
    getDocStatusColor,
    type GuaranteeItem,
    type GuaranteeStatus,
    type TraderStatus,
    type GuaranteeValidationStatus,
    type DocStatus,
    type SefazStatus,
    type AnalysisCriteria,
} from './collateralUtils';

const GUARANTEE_STATUSES: GuaranteeStatus[] = ['TO DO', 'EM ANDAMENTO', 'CONFERIDO', 'CONCLUIDO', 'QUITADA', 'QUITADA PARCIAL', 'PAUSADO', 'ACE', 'N/A'];
const TRADER_STATUSES: TraderStatus[] = ['CONCLUIDO', 'AGUARDANDO DOC - Área Informada', 'TEM PENDÊNCIA', 'N/A'];
const VALIDATION_STATUSES: GuaranteeValidationStatus[] = ['TO DO', 'EM ANDAMENTO', 'CONCLUÍDO', 'PARCIALMENTE VALIDADA', 'PAUSADO', 'N/A'];
const DOC_STATUSES: DocStatus[] = ['OBRIGATORIO', 'SIM', 'SIM, ANTES', 'NÃO', 'CONTRATO', 'N/A'];
const SEFAZ_STATUSES: SefazStatus[] = ['FAZER', 'FEITA', 'N/A'];
const CRITERIA_OPTIONS: AnalysisCriteria[] = ['Completa', 'Amostragem', 'N/A'];

const Select = ({ label, value, options, onChange, colorFn }: { label: string; value: string; options: string[]; onChange: (v: string) => void; colorFn?: (v: string) => string }) => (
    <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
        <select value={value} onChange={e => onChange(e.target.value)}
            className={`w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white ${colorFn ? colorFn(value) : ''}`}>
            <option value="">—</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
    </div>
);

const Input = ({ label, value, onChange, type = 'text' }: { label: string; value: string | number; onChange: (v: string) => void; type?: string }) => (
    <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
    </div>
);

const DocRow = ({ label, status, archived, pending, onStatusChange, onArchivedChange, onPendingChange }: {
    label: string; status: string; archived: number; pending: number;
    onStatusChange: (v: string) => void; onArchivedChange: (v: string) => void; onPendingChange: (v: string) => void;
}) => (
    <div className="grid grid-cols-4 gap-2 items-end">
        <Select label={label} value={status} options={[...DOC_STATUSES]} onChange={onStatusChange} colorFn={v => getDocStatusColor(v as DocStatus)} />
        <Input label="Arquivados" value={archived} onChange={onArchivedChange} type="number" />
        <Input label="Pendentes" value={pending} onChange={onPendingChange} type="number" />
        <div className="text-xs text-gray-500 dark:text-gray-400 pb-2">
            Total: {archived + pending}
        </div>
    </div>
);

const GuaranteeModalEdit = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const context = useOutletContext<{ onDataChange?: () => void }>();
    const onDataChange = context?.onDataChange;

    const item = getGuarantees().find(g => g.id === Number(id));
    const [form, setForm] = useState<GuaranteeItem>(item ? { ...item } : {} as GuaranteeItem);

    if (!item) return <div className="p-4 text-red-600">Item não encontrado</div>;
    const set = (field: keyof GuaranteeItem, value: any) => setForm(prev => ({ ...prev, [field]: value }));

    const handleSave = () => {
        updateGuarantee(form.id, form);
        toast.success('Garantia atualizada!');
        onDataChange?.();
        navigate(-1);
    };

    return (
        <PresentationModal id="guarantee-edit" show={true} onClose={() => navigate(-1)}
            header={`Editar Garantia — ${form.operationCode}`}>
            <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                {/* Header info */}
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><span className="text-gray-500 dark:text-gray-400">Cedente:</span> <span className="font-medium dark:text-white">{form.assignor}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">CNPJ:</span> <span className="dark:text-white">{form.assignorCnpj}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Tipo:</span> <span className="dark:text-white">{form.operationType}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Títulos:</span> <span className="dark:text-white">{form.totalTitles}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Valor:</span> <span className="font-medium dark:text-white">{formatCurrency(form.totalAmount)}</span></div>
                        <div>
                            <span className="text-gray-500 dark:text-gray-400">Intensidade: </span>
                            {form.analysisIntensity && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getIntensityColor(form.analysisIntensity)}`}>{form.analysisIntensity}</span>}
                        </div>
                    </div>
                </div>

                {/* Status section */}
                <fieldset className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                    <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300 px-2">Status & Responsáveis</legend>
                    <div className="grid grid-cols-3 gap-4">
                        <Select label="Status Garantia" value={form.statusGuarantee} options={GUARANTEE_STATUSES} onChange={v => set('statusGuarantee', v)} colorFn={v => getGuaranteeStatusColor(v as GuaranteeStatus)} />
                        <Select label="Status Trader" value={form.statusTrader} options={TRADER_STATUSES} onChange={v => set('statusTrader', v)} />
                        <Select label="Status Validação" value={form.statusValidation} options={VALIDATION_STATUSES} onChange={v => set('statusValidation', v)} />
                        <Input label="Analista Garantias" value={form.analystName} onChange={v => set('analystName', v)} />
                        <Input label="Trader" value={form.traderName} onChange={v => set('traderName', v)} />
                        <Input label="Analista Validação" value={form.validationAnalystName} onChange={v => set('validationAnalystName', v)} />
                        <Select label="Obrigação" value={form.obligation} options={['COM REGRESSO', 'SEM REGRESSO', 'SEM GARANTIA']} onChange={v => set('obligation', v)} />
                        <Select label="Critério Análise" value={form.analysisCriteria} options={CRITERIA_OPTIONS} onChange={v => set('analysisCriteria', v)} />
                        <Input label="Data com Trader" value={form.traderHandoverDate} onChange={v => set('traderHandoverDate', v)} type="date" />
                    </div>
                </fieldset>

                {/* Document checklist */}
                <fieldset className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                    <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300 px-2">Checklist Documental</legend>
                    <div className="space-y-3">
                        <DocRow label="XML" status={form.xml} archived={form.xmlArchived} pending={form.xmlPending}
                            onStatusChange={v => set('xml', v)} onArchivedChange={v => set('xmlArchived', +v)} onPendingChange={v => set('xmlPending', +v)} />
                        <DocRow label="NF PDF" status={form.nfPdf} archived={form.nfPdfArchived} pending={form.nfPdfPending}
                            onStatusChange={v => set('nfPdf', v)} onArchivedChange={v => set('nfPdfArchived', +v)} onPendingChange={v => set('nfPdfPending', +v)} />
                        <DocRow label="Canhoto" status={form.canhoto} archived={form.canhotoArchived} pending={form.canhotoPending}
                            onStatusChange={v => set('canhoto', v)} onArchivedChange={v => set('canhotoArchived', +v)} onPendingChange={v => set('canhotoPending', +v)} />
                        <DocRow label="Duplicata c/ Aceite" status={form.duplicata} archived={form.duplicataArchived} pending={form.duplicataPending}
                            onStatusChange={v => set('duplicata', v)} onArchivedChange={v => set('duplicataArchived', +v)} onPendingChange={v => set('duplicataPending', +v)} />
                        <div className="grid grid-cols-4 gap-2 items-end">
                            <Select label="Validação Ligação/Email" value={form.callValidation} options={[...DOC_STATUSES]} onChange={v => set('callValidation', v)} />
                            <Input label="Confirmados" value={form.callConfirmed} onChange={v => set('callConfirmed', +v)} type="number" />
                            <Input label="Pendentes" value={form.callPending} onChange={v => set('callPending', +v)} type="number" />
                            <div />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <Select label="Contrato Compra/Venda" value={form.purchaseContract} options={['SIM', 'NÃO']} onChange={v => set('purchaseContract', v)} />
                            <Select label="SEFAZ (amostragem)" value={form.sefaz} options={SEFAZ_STATUSES} onChange={v => set('sefaz', v)} />
                            <Input label="Títulos Amostragem" value={form.sampledTitles} onChange={v => set('sampledTitles', +v)} type="number" />
                        </div>
                    </div>
                </fieldset>

                {/* Additional guarantees */}
                <fieldset className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                    <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300 px-2">Garantias Adicionais & Cartório</legend>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Outras Garantias?</label>
                            <label className="flex items-center gap-2 text-sm dark:text-white">
                                <input type="checkbox" checked={form.additionalGuarantees} onChange={e => set('additionalGuarantees', e.target.checked)} className="rounded" />
                                Sim
                            </label>
                        </div>
                        {form.additionalGuarantees && (
                            <>
                                <Input label="Tipo" value={form.additionalGuaranteeType} onChange={v => set('additionalGuaranteeType', v)} />
                                <Input label="Valor" value={form.additionalGuaranteeValue} onChange={v => set('additionalGuaranteeValue', +v)} type="number" />
                            </>
                        )}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Registro em Cartório?</label>
                            <label className="flex items-center gap-2 text-sm dark:text-white">
                                <input type="checkbox" checked={form.courtRegistration} onChange={e => set('courtRegistration', e.target.checked)} className="rounded" />
                                Sim
                            </label>
                        </div>
                        {form.courtRegistration && (
                            <Input label="Contrato a Registrar" value={form.contractToRegister} onChange={v => set('contractToRegister', v)} />
                        )}
                    </div>
                </fieldset>

                {/* Notes */}
                <fieldset className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                    <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300 px-2">Observações</legend>
                    <div className="space-y-3">
                        <Input label="Pendência" value={form.pendency} onChange={v => set('pendency', v)} />
                        <Input label="Follow Up Áreas" value={form.followUpAreas} onChange={v => set('followUpAreas', v)} />
                        <Input label="Detalhe da Checagem" value={form.checkDetail} onChange={v => set('checkDetail', v)} />
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Observação</label>
                            <textarea value={form.observation} onChange={e => set('observation', e.target.value)} rows={3}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
                        </div>
                    </div>
                </fieldset>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-2">
                    <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600">Cancelar</button>
                    <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Salvar</button>
                </div>
            </div>
        </PresentationModal>
    );
};

export default GuaranteeModalEdit;
